import {
  AppShellRailTile,
  Box,
  Button,
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  DrawerTrigger,
  Icon,
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useIsMobile,
} from "@salestrak/ui";
import * as React from "react";

import { CustomizeNavigationDialog } from "./customize-navigation-dialog";
import { useModuleRail } from "./module-rail-context";
import type { ResolvedRailTile } from "#app/lib/hooks/use-nav-preferences";
import type { Omittable } from "@salestrak/ui";

/**
 * MoreModulesTile — the fixed "More" rail tile. Opens an overlay listing the
 * overflow modules (tiles the user hid from the rail) and a Customize Sidebar
 * action:
 * a menu anchored to the tile on desktop — the same chrome as the workspace
 * menu at the head of this rail — a drawer on mobile. The overflow list and
 * its press handler come from `ModuleRail`, which owns route resolution
 * (e.g. Workflows → first board) and the drawer-keep-open semantics shared
 * with the visible tiles.
 */
export function MoreModulesTile({
  overflowTiles,
  onTilePress,
  onInvitePress,
  popoverTopInset = 0,
  compact: compactProp,
  trigger,
}: Omittable<{
  overflowTiles: ResolvedRailTile[];
  onTilePress: (tile: ResolvedRailTile) => void;
  /** When set, an Invite row renders after the overflow tiles — the rail
   * passes it when its column is too short to fit the pinned Invite tile. */
  onInvitePress?: () => void;
  /** Window-Y of the rail's top edge. Fed to the popup as a top collision
   * inset so collision-avoidance never shifts it above the rail. */
  popoverTopInset?: number;
  /** Overrides the context compact preference so this tile matches the tiles
   * around it — the module strip's filled trigger is always compact. */
  compact?: boolean;
  /** Replaces the default `AppShellRailTile` trigger — the module strip passes its
   * own filled tile so the More tile matches the tiles around it. */
  trigger?: React.ReactElement;
}>) {
  const isMobile = useIsMobile();
  const { compact: compactPreference } = useModuleRail();
  const compact = compactProp ?? compactPreference;
  const [open, setOpen] = React.useState(false);
  const [customizeOpen, setCustomizeOpen] = React.useState(false);

  const handleCustomize = () => {
    // Swap the menu/drawer for the customize dialog.
    setOpen(false);
    setCustomizeOpen(true);
  };

  const handleTilePress = (tile: ResolvedRailTile) => {
    setOpen(false);
    onTilePress(tile);
  };

  const handleInvitePress = () => {
    setOpen(false);
    onInvitePress?.();
  };

  const tile = trigger ?? (
    <AppShellRailTile compact={compact} icon="grid-dots" label="More" />
  );

  // Each section renders only when it has rows. Customize Sidebar is
  // unconditional, so the popup is never empty and no section needs an
  // empty-state line of its own.
  const hasModuleRows = overflowTiles.length > 0 || onInvitePress != null;

  return (
    <>
      {isMobile ? (
        <Drawer onOpenChange={setOpen} open={open}>
          <DrawerTrigger render={tile} />
          {/* Nav chrome paints `sidebar`, like the AppShell nav drawer. */}
          <DrawerPopup showBar surface="sidebar">
            <DrawerHeader>
              <DrawerTitle>More modules</DrawerTitle>
            </DrawerHeader>
            <DrawerPanel>
              <Box direction="column" gap="md">
                {hasModuleRows && (
                  <Box direction="column" gap="sm">
                    {overflowTiles.map((overflowTile, index) => (
                      <Button
                        justify="start"
                        fullWidth
                        key={`${overflowTile.id}-${index}`}
                        onClick={() => handleTilePress(overflowTile)}
                        // Nav rows, not actions: the label rests at full ink
                        // like every sidebar row.
                        ink="foreground"
                        variant="ghost"
                      >
                        <Icon name={overflowTile.icon} size="sm" />
                        <Text>{overflowTile.label}</Text>
                      </Button>
                    ))}
                    {onInvitePress != null && (
                      <Button
                        justify="start"
                        fullWidth
                        onClick={handleInvitePress}
                        ink="foreground"
                        variant="ghost"
                      >
                        <Icon name="user-plus" size="sm" />
                        <Text>Invite</Text>
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            </DrawerPanel>
            <DrawerFooter variant="bare">
              <Button
                fullWidth
                onClick={handleCustomize}
                shape="square"
                color="neutral"
              >
                <Icon name="settings-2" size="sm" />
                <Text>Customize Sidebar</Text>
              </Button>
            </DrawerFooter>
          </DrawerPopup>
        </Drawer>
      ) : (
        <Menu onOpenChange={setOpen} open={open}>
          {/* Named on hover like every other rail item. Desktop only — the
            * mobile branch is a Drawer, where there is no hover to name it. */}
          <Tooltip>
            <TooltipTrigger render={<MenuTrigger render={tile} />} />
            <TooltipPopup side="right">More</TooltipPopup>
          </Tooltip>
          <MenuPopup
            align="start"
            // Keep the popup's top at or below the rail's top edge on short
            // screens. The popup's own scroller absorbs the lost height.
            collisionPadding={{ top: popoverTopInset }}
            side="right"
            sideOffset={8}
            width="action"
          >
            {hasModuleRows && (
              <>
                <MenuGroup>
                  <MenuGroupLabel>More modules</MenuGroupLabel>
                  {overflowTiles.map((overflowTile, index) => (
                    <MenuItem
                      key={`${overflowTile.id}-${index}`}
                      onClick={() => handleTilePress(overflowTile)}
                    >
                      <Icon color="muted" name={overflowTile.icon} size="md" />
                      <Text>{overflowTile.label}</Text>
                    </MenuItem>
                  ))}
                  {onInvitePress != null && (
                    <MenuItem onClick={handleInvitePress}>
                      <Icon color="muted" name="user-plus" size="md" />
                      <Text>Invite</Text>
                    </MenuItem>
                  )}
                </MenuGroup>
                <MenuSeparator />
              </>
            )}
            <MenuItem onClick={handleCustomize}>
              <Icon color="muted" name="settings-2" size="md" />
              <Text>Customize Sidebar</Text>
            </MenuItem>
          </MenuPopup>
        </Menu>
      )}
      <CustomizeNavigationDialog
        initialTab="navigation"
        onOpenChange={setCustomizeOpen}
        open={customizeOpen}
      />
    </>
  );
}
